import { useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { usePhotos } from '../../hooks/usePhotos';
import { useAutoSelectPrimary } from '../../hooks/useAutoSelectPrimary';
import styles from './LocationManager.module.css';

export default function LocationManager() {
  const { state, derived, activeHunt } = useAppContext();
  const { locationsList, sharedLocations, photosByItem } = derived;
  const { updatePhoto } = usePhotos();
  const { runAutoSelect } = useAutoSelectPrimary();

  const [editingLoc, setEditingLoc]     = useState(null); // location name being renamed
  const [editValue,  setEditValue]      = useState('');
  const [mergeTarget, setMergeTarget]   = useState({}); // { [sourceLoc]: targetLoc }
  const [autoSelecting, setAutoSelecting] = useState(false);
  const [autoSelectResult, setAutoSelectResult] = useState('');

  const huntPhotos = state.photos.filter(p => p.huntId === activeHunt?.id);

  async function handleAutoSelect() {
    setAutoSelecting(true);
    setAutoSelectResult('');
    try {
      const { updated, total } = await runAutoSelect();
      setAutoSelectResult(
        updated === 0
          ? `Already optimal — no changes needed across ${total} item${total !== 1 ? 's' : ''}.`
          : `Updated primary photo for ${updated} of ${total} item${total !== 1 ? 's' : ''}.`
      );
    } finally {
      setAutoSelecting(false);
    }
  }

  // Count stats per location
  function getStats(loc) {
    const allPhotos = huntPhotos.filter(p => p.location === loc);
    const primaryCount = [...photosByItem.values()].filter(photos =>
      (photos.find(p => p.isPrimary) ?? photos[0])?.location === loc
    ).length;
    return { total: allPhotos.length, primaryCount };
  }

  async function handleRename(oldName) {
    const newName = editValue.trim();
    if (!newName || newName === oldName) { setEditingLoc(null); return; }
    // Update all photos with this location
    const affected = huntPhotos.filter(p => p.location === oldName);
    for (const photo of affected) {
      await updatePhoto(photo.id, { location: newName });
    }
    setEditingLoc(null);
  }

  async function handleMerge(sourceLoc) {
    const target = mergeTarget[sourceLoc];
    if (!target || target === sourceLoc) return;
    const affected = huntPhotos.filter(p => p.location === sourceLoc);
    for (const photo of affected) {
      await updatePhoto(photo.id, { location: target });
    }
    setMergeTarget(prev => { const n = { ...prev }; delete n[sourceLoc]; return n; });
  }

  const sharedCount = [...locationsList].filter(l => sharedLocations.has(l)).length;

  return (
    <div className={styles.view}>
      <div className={styles.page}>
        <h2 className={styles.pageTitle}>Locations</h2>

        {sharedCount > 0 && (
          <div className={styles.warningBanner}>
            {sharedCount} location{sharedCount !== 1 ? 's are' : ' is'} primary for multiple items
          </div>
        )}

        <div className={styles.autoSelectRow}>
          <button
            className={styles.autoSelectBtn}
            onClick={handleAutoSelect}
            disabled={autoSelecting}
          >
            {autoSelecting ? 'Selecting…' : 'Auto-select primary photos'}
          </button>
          {autoSelectResult && <span className={styles.autoSelectResult}>{autoSelectResult}</span>}
        </div>

        <div className={styles.list}>
          {locationsList.map(loc => {
            const { total, primaryCount } = getStats(loc);
            const isShared = sharedLocations.has(loc);
            const isEditing = editingLoc === loc;

            return (
              <div key={loc} className={`${styles.row} ${isShared ? styles.sharedRow : ''}`}>
                <div className={styles.locInfo}>
                  {isEditing ? (
                    <form
                      onSubmit={e => { e.preventDefault(); handleRename(loc); }}
                      className={styles.renameForm}
                    >
                      <input
                        className={styles.renameInput}
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        autoFocus
                        onBlur={() => handleRename(loc)}
                      />
                    </form>
                  ) : (
                    <button
                      className={styles.locName}
                      onClick={() => { setEditingLoc(loc); setEditValue(loc); }}
                      title="Click to rename"
                    >
                      {loc}
                    </button>
                  )}
                  <div className={styles.locMeta}>
                    <span>{total} photo{total !== 1 ? 's' : ''}</span>
                    <span className={styles.sep}>·</span>
                    <span>primary for {primaryCount}</span>
                    {isShared && (
                      <span className={styles.sharedLabel}>×2 shared primary</span>
                    )}
                  </div>
                </div>

                <div className={styles.mergeRow}>
                  <select
                    className={styles.mergeSelect}
                    value={mergeTarget[loc] ?? ''}
                    onChange={e => setMergeTarget(prev => ({ ...prev, [loc]: e.target.value }))}
                  >
                    <option value="">Merge into…</option>
                    {locationsList.filter(l => l !== loc).map(l => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                  {mergeTarget[loc] && (
                    <button
                      className={styles.mergeBtn}
                      onClick={() => {
                        if (window.confirm(`Merge "${loc}" into "${mergeTarget[loc]}"? This will update ${total} photos.`)) {
                          handleMerge(loc);
                        }
                      }}
                    >
                      Merge
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {locationsList.length === 0 && (
            <p className={styles.empty}>No locations yet. Add some photos first.</p>
          )}
        </div>
      </div>
    </div>
  );
}
